import {
  lerSolicitacaoPdfCertificado,
  type SolicitacaoPdfCertificadoAcessos,
} from '@/features/arbitral-detalhes';
import { portalGet, portalPost } from '@/features/portal/utils/portalRequest';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import type { CertificadoAcessosSentenca } from '../../types/certificadoAcessosSentenca.types';
import { certificadoAcessosSentencaDtoSchema } from '../../types/certificadoAcessosSentencaDto.schema';
import type { OrigemDocumentoEncerramento } from '../../types/encerramento.types';
import { mapCertificadoAcessosSentenca } from '../../utils/certificadoAcessosSentencaMapper';

const TIMEOUT_MS = 10000;

// Cinco endereços, um por papel, MESMO documento (rn8) — no desenho das cinco portas do
// Documento da Sentença. O `:id` da superfície do notificado é a parteArbitral (o portal inteiro
// navega por ela); nas demais é o processo. As duas superfícies de portal autenticam pela sessão
// do portal (portalGet), as outras três pelo JWT do workspace.
const PATH_POR_ORIGEM: Record<OrigemDocumentoEncerramento, (id: string) => string> = {
  secretaria: (id) => `/admin/arbitral/processos/${id}/certificado-acessos-sentenca`,
  parte: (id) => `/arbitral/processos/${id}/certificado-acessos-sentenca`,
  arbitro: (id) => `/arbitral/painel/processos/${id}/certificado-acessos-sentenca`,
  advogado: (id) => `/arbitral/portal/advogados/processos/${id}/certificado-acessos-sentenca`,
  notificado: (id) => `/arbitral/portal/citacoes/${id}/certificado-acessos-sentenca`,
};

const USA_SESSAO_DO_PORTAL: Record<OrigemDocumentoEncerramento, boolean> = {
  secretaria: false,
  parte: false,
  arbitro: false,
  advogado: true,
  notificado: true,
};

export class CertificadoSentencaSemPermissaoError extends Error {
  constructor() {
    super('Você não tem permissão para visualizar este certificado.');
    this.name = 'CertificadoSentencaSemPermissaoError';
  }
}

// 404 = a sentença ainda não foi publicada (rn8), então não há comprovação a exibir — estado
// Bloqueado, nunca documento em branco.
export class SentencaNaoProferidaError extends Error {
  constructor() {
    super('A sentença ainda não foi proferida neste processo, então não há comprovação a exibir.');
    this.name = 'SentencaNaoProferidaError';
  }
}

const ERRO_GENERICO = 'Erro ao carregar o Certificado de Acessos da Sentença';
const ERRO_PDF = 'Erro ao gerar o PDF do Certificado de Acessos da Sentença';

/**
 * Pede o PDF na própria rota de cada papel, com a própria sessão. O do notificado é a cópia
 * redigida da parte dele; os outros quatro, a peça integral — a mesma que cada um já lê na tela.
 * O `signal` de quem pediu entra junto com o timeout: sair da tela cancela também o POST em voo.
 */
function solicitarPdfNaPorta(
  origem: OrigemDocumentoEncerramento,
  id: string,
  signal: AbortSignal,
): Promise<Response> {
  const path = `${PATH_POR_ORIGEM[origem](id)}/gerar-pdf`;
  const sinal = AbortSignal.any([signal, AbortSignal.timeout(TIMEOUT_MS)]);
  return USA_SESSAO_DO_PORTAL[origem]
    ? portalPost(path, undefined, false, undefined, sinal)
    : apiPost(path, undefined, false, sinal);
}

export const certificadoAcessosSentencaService = {
  getCertificado: async (
    origem: OrigemDocumentoEncerramento,
    id: string,
  ): Promise<CertificadoAcessosSentenca> => {
    const path = PATH_POR_ORIGEM[origem](id);
    let response: Response;
    try {
      response = USA_SESSAO_DO_PORTAL[origem]
        ? await portalGet(path, undefined, false, undefined, TIMEOUT_MS)
        : await apiGet(path, undefined, false, AbortSignal.timeout(TIMEOUT_MS));
    } catch {
      throw new Error(ERRO_GENERICO);
    }

    if (response.status === 403) throw new CertificadoSentencaSemPermissaoError();
    if (response.status === 404) throw new SentencaNaoProferidaError();
    if (!response.ok) throw new Error(ERRO_GENERICO);

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new Error(ERRO_GENERICO);
    }
    if (typeof body !== 'object' || body === null || !('data' in body)) {
      throw new Error(ERRO_GENERICO);
    }
    const dto = certificadoAcessosSentencaDtoSchema.safeParse(body.data);
    if (!dto.success) throw new Error(ERRO_GENERICO);
    return mapCertificadoAcessosSentenca(dto.data);
  },

  /**
   * O PDF emitido no servidor — o único que o `/autenticar` reconhece, porque é dele que o hash foi
   * gravado na emissão. O endpoint é idempotente (trava e relê a certidão antes de emitir outra),
   * então reperguntar durante o `GENERATING` não multiplica peças.
   */
  solicitarPdf: async (
    origem: OrigemDocumentoEncerramento,
    id: string,
    signal: AbortSignal,
  ): Promise<SolicitacaoPdfCertificadoAcessos> =>
    lerSolicitacaoPdfCertificado(
      await solicitarPdfNaPorta(origem, id, signal),
      ERRO_PDF,
      'Link do Certificado de Acessos da Sentença',
    ),
};
